import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HeroBlock from './blocks/HeroBlock';
import FormsBlock from './blocks/FormsBlock';
import ReviewsBlock from './blocks/ReviewsBlock';
import ProductsBlock from './blocks/ProductsBlock';
import CalendarBlock from './blocks/CalendarBlock';
import ColumnBlock from './blocks/ColumnBlock';

const PageBuilder = ({ initialBlocks = [], onSave, onCancel }) => {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const availableBlocks = [
    {
      id: 'hero',
      name: 'Hero Section',
      description: 'Add a hero section with image/video, text, and CTA',
      icon: '🎯',
      component: HeroBlock
    },
    {
      id: 'columns',
      name: 'Column Layout',
      description: 'Create 1, 2, or 3 column layouts for content',
      icon: '📊',
      component: ColumnBlock
    },
    {
      id: 'forms',
      name: 'Forms',
      description: 'Add contact forms or other form types',
      icon: '📝',
      component: FormsBlock
    },
    {
      id: 'reviews',
      name: 'Reviews Section',
      description: 'Display customer reviews and testimonials',
      icon: '⭐',
      component: ReviewsBlock
    },
    {
      id: 'products',
      name: 'Product Display',
      description: 'Show products with configurable count and layout',
      icon: '🛍️',
      component: ProductsBlock
    },
    {
      id: 'calendar',
      name: 'Calendar Block',
      description: 'Add appointment booking calendar',
      icon: '📅',
      component: CalendarBlock
    }
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addBlock = (blockType) => {
    const blockTemplate = availableBlocks.find(b => b.id === blockType);
    if (!blockTemplate) return;

    const newBlock = {
      id: `${blockType}-${Date.now()}`,
      type: blockType,
      data: getDefaultBlockData(blockType),
      order: blocks.length
    };

    setBlocks([...blocks, newBlock]);
  };

  const getDefaultBlockData = (blockType) => {
    const defaults = {
      hero: {
        title: 'Welcome to Our Store',
        subtitle: 'Discover amazing products and services',
        backgroundType: 'image',
        backgroundImage: '',
        backgroundVideo: '',
        ctaText: 'Shop Now',
        ctaLink: '/shop',
        textColor: '#ffffff',
        overlayOpacity: 0.5
      },
      columns: {
        columnCount: 2,
        title: '',
        titleAlign: 'left',
        titleColor: '#333',
        column1Content: 'Add your content here...',
        column2Content: 'Add your content here...',
        column3Content: 'Add your content here...',
        gap: '1rem',
        backgroundColor: '#ffffff',
        columnBackground: '#ffffff',
        columnPadding: '1.5rem',
        columnBorderRadius: '0.5rem',
        columnShadow: '0 2px 4px rgba(0,0,0,0.1)',
        columnBorder: '1px solid #e5e7eb',
        columnTextColor: '#333'
      },
      forms: {
        formType: 'contact',
        title: 'Get in Touch',
        description: 'Send us a message and we\'ll get back to you',
        fields: ['name', 'email', 'message'],
        submitText: 'Send Message',
        successMessage: 'Thank you for your message!'
      },
      reviews: {
        title: 'What Our Customers Say',
        description: 'Read reviews from our satisfied customers',
        reviews: [],
        showRatings: true,
        maxReviews: 6
      },
      products: {
        title: 'Featured Products',
        description: 'Check out our best-selling products',
        productCount: 6,
        category: 'all',
        sortBy: 'featured',
        showPrice: true,
        showDescription: true
      },
      calendar: {
        title: 'Book an Appointment',
        description: 'Schedule a consultation with our experts',
        serviceId: '',
        showTimeSlots: true,
        allowRescheduling: true
      }
    };

    return defaults[blockType] || {};
  };

  const updateBlock = (blockId, newData) => {
    setBlocks(blocks.map(block => 
      block.id === blockId 
        ? { ...block, data: { ...block.data, ...newData } }
        : block
    ));
  };

  const removeBlock = (blockId) => {
    setBlocks(blocks.filter(block => block.id !== blockId));
    if (selectedBlock === blockId) {
      setSelectedBlock(null);
    }
  };

  const duplicateBlock = (blockId) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;

    const newBlock = {
      ...block,
      id: `${block.type}-${Date.now()}`,
      order: blocks.length
    };

    setBlocks([...blocks, newBlock]);
  };

  // Sortable Block Component
  const SortableBlock = ({ block }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: block.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const blockTemplate = availableBlocks.find(b => b.id === block.type);
    if (!blockTemplate) return null;

    const BlockComponent = blockTemplate.component;
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <BlockComponent
          data={block.data}
          isSelected={selectedBlock === block.id}
          onSelect={() => setSelectedBlock(block.id)}
          onUpdate={(newData) => updateBlock(block.id, newData)}
          isPreview={isPreviewMode}
        />
      </div>
    );
  };

  const renderBlock = (block) => {
    return <SortableBlock key={block.id} block={block} />;
  };

  const handleSave = () => {
    onSave(blocks);
  };

  if (isPreviewMode) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 1000,
          display: 'flex',
          gap: '0.5rem'
        }}>
          <button
            onClick={() => setIsPreviewMode(false)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        </div>
        {blocks.map(renderBlock)}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div style={{
        width: '300px',
        backgroundColor: '#f8f9fa',
        borderRight: '1px solid #dee2e6',
        padding: '1rem',
        overflowY: 'auto'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
          Page Builder
        </h3>
        
        {/* Available Blocks */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600' }}>
            Add Blocks
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {availableBlocks.map(block => (
              <button
                key={block.id}
                onClick={() => addBlock(block.id)}
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.backgroundColor = '#f8f9ff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>{block.icon}</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                      {block.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                      {block.description}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Block List */}
        <div>
          <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600' }}>
            Page Blocks ({blocks.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {blocks.map((block, index) => {
              const blockTemplate = availableBlocks.find(b => b.id === block.type);
              return (
                <div
                  key={block.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: selectedBlock === block.id ? '#e3f2fd' : 'white',
                    border: `1px solid ${selectedBlock === block.id ? '#2196f3' : '#dee2e6'}`,
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedBlock(block.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1rem' }}>{blockTemplate?.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        {blockTemplate?.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        Block {index + 1}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateBlock(block.id);
                        }}
                        style={{
                          padding: '0.25rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        title="Duplicate"
                      >
                        📋
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeBlock(block.id);
                        }}
                        style={{
                          padding: '0.25rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                        title="Remove"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{
          padding: '1rem',
          backgroundColor: 'white',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            Page Builder
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setIsPreviewMode(true)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Preview
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Save Page
            </button>
            <button
              onClick={onCancel}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div style={{ flex: 1, padding: '1rem', backgroundColor: '#f8f9fa' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={blocks.map(block => block.id)} strategy={verticalListSortingStrategy}>
              <div style={{ minHeight: '400px' }}>
                {blocks.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    color: '#6c757d'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                    <h3 style={{ marginBottom: '0.5rem' }}>Start Building Your Page</h3>
                    <p>Add blocks from the sidebar to create your custom page</p>
                  </div>
                ) : (
                  blocks.map((block) => (
                    <div key={block.id} style={{ marginBottom: '1rem' }}>
                      {renderBlock(block)}
                    </div>
                  ))
                )}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default PageBuilder;
